// <pfs-status-dot>: der kleine Status-Kreis für Fortschritts-/Checklisten-Zeilen
// (PFS-729). Ein präsentatives Primitiv — done ✓ / active (Halo) / open (Symbol
// oder leer) / error ! / pending (Spinner). BEWUSST DUMM: hält keinen State, der
// Host schiebt den Zustand per data-* rein (uniform mit avatar/badge/stepper).
//
// pending KOMPONIERT das bestehende <pfs-loading-indicator> (inline) — kein
// eigener Spinner (Reuse-Kanon). Wiederverwendbar außerhalb des Steppers:
// Listen-Zeilen, Timelines, Checklisten, Anforderungslisten.
//
// Attribute (alle data-*):
//   data-state   "done" | "active" | "open" | "error" | "pending" (default "open")
//   data-symbol  Inhalt für open/active (z.B. Schritt-Nummer). Leer = leerer Kreis.
//
// Größen-Naht: --pfs-status-dot-size (default 1.75rem) überschreibbar vom Host.
// Brandbar: ausschließlich --pfs-*-Tokens (auch im Dark prüfen).

import { defineComponent } from "../../composition/define-component.js";
import { withShadow } from "../../composition/with-shadow.js";
import { SHADOW_RESET_CSS } from "../../styles/shadow-reset.js";
import { escapeHTML } from "../../utils/escape-html.js";

const STYLES = `
${SHADOW_RESET_CSS}
:host { display: inline-flex; --_s: var(--pfs-status-dot-size, 1.75rem); }

.dot {
  width: var(--_s);
  height: var(--_s);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  font-size: 0.8125rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  background: var(--pfs-color-surface-sunken);
  border: 1.5px solid var(--pfs-color-outline);
  color: var(--pfs-color-on-surface-subtle);
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}

:host([data-state="done"]) .dot {
  background: var(--pfs-color-success);
  border-color: var(--pfs-color-success);
  color: var(--pfs-color-on-primary);
}
:host([data-state="active"]) .dot {
  background: color-mix(in srgb, var(--pfs-color-primary) 14%, transparent);
  border-color: var(--pfs-color-primary);
  color: var(--pfs-color-primary);
  box-shadow: 0 0 0 4px color-mix(in srgb, var(--pfs-color-primary) 20%, transparent);
}
:host([data-state="error"]) .dot {
  background: var(--pfs-color-danger-container);
  border-color: var(--pfs-color-danger);
  color: var(--pfs-color-on-danger-container);
}
:host([data-state="pending"]) .dot {
  background: transparent;
  border-color: transparent;
  color: var(--pfs-color-primary);
}
/* der eingebettete Loading-Indicator füllt den Kreis kompakt */
.dot pfs-loading-indicator { --pfs-loading-min-height: 0; }
`;

const CHECK = `<svg viewBox="0 0 16 16" width="14" height="14" fill="none" aria-hidden="true"><path d="M3.5 8.5l3 3 6-7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const BANG = `<svg viewBox="0 0 16 16" width="14" height="14" fill="none" aria-hidden="true"><path d="M8 4v5M8 12h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

defineComponent({
  tagName: "pfs-status-dot",
  mixins: [withShadow()],
  render(host, ctx) {
    const root = ctx.shadowRoot;
    if (!root) return;
    const style = document.createElement("style");
    style.textContent = STYLES;
    const dot = document.createElement("span");
    dot.className = "dot";
    dot.setAttribute("aria-hidden", "true");
    root.append(style, dot);

    const paint = (): void => {
      const state = host.dataset.state ?? "open";
      const symbol = host.dataset.symbol ?? "";
      if (state === "done") {
        dot.innerHTML = CHECK;
      } else if (state === "error") {
        dot.innerHTML = BANG;
      } else if (state === "pending") {
        // pending komponiert pfs-loading-indicator (inline, leerer Text = nur Spinner)
        dot.innerHTML = `<pfs-loading-indicator data-variant="inline" data-text=""></pfs-loading-indicator>`;
      } else {
        dot.innerHTML = escapeHTML(symbol);
      }
    };
    paint();
    const obs = new MutationObserver(paint);
    ctx.cleanup.push(() => obs.disconnect());
    obs.observe(host, { attributes: true, attributeFilter: ["data-state", "data-symbol"] });
  },
});
